import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  inject,
} from '@angular/core';

// Copy of the horizon-shell lang-segment; this app must not depend on the shared library.
@Component({
    selector: 'app-lang-segment',
    templateUrl: './lang-segment.component.html',
    styleUrls: ['./lang-segment.component.scss'],
    standalone: true,
    encapsulation: ViewEncapsulation.None
})
export class LangSegmentComponent implements AfterViewInit, OnChanges {
  private static readonly INSET_PX = 3;

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() locale = 'en';
  @Input() locales: [string, string] = ['en', 'de'];

  @Input() labels: [string, string] = ['En', 'De'];

  @Output() readonly localeChange = new EventEmitter<string>();

  @HostBinding('class.lang-segment') readonly segmentClass = true;
  @HostBinding('class.lang-segment--dragging') dragging = false;

  indicatorTransform = 'translateX(0px)';
  firstActive = true;
  secondActive = false;

  private travelPx = 0;
  private dragStartX = 0;
  private dragStartOffset = 0;
  private dragOffset = 0;
  private activePointerId: number | null = null;
  private moved = false;
  private suppressClick = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (this.dragging) return;
    if (changes['locale'] || changes['locales']) {
      this.syncVisualState();
    }
  }

  ngAfterViewInit(): void {
    this.syncVisualState();
    this.cdr.detectChanges();
  }

  @HostListener('pointerdown', ['$event'])
  onPointerDown(event: PointerEvent): void {
    if (event.button !== 0) return;

    event.stopPropagation();

    this.suppressClick = false;
    this.updateTravel();
    this.dragging = true;
    this.moved = false;
    this.dragStartX = event.clientX;
    this.dragStartOffset = this.isSecond(this.locale) ? this.travelPx : 0;
    this.dragOffset = this.dragStartOffset;
    this.activePointerId = event.pointerId;
    this.host.nativeElement.setPointerCapture(event.pointerId);
    this.applyDragTransform(this.dragOffset);
  }

  @HostListener('pointermove', ['$event'])
  onPointerMove(event: PointerEvent): void {
    if (!this.dragging || event.pointerId !== this.activePointerId) return;

    const delta = event.clientX - this.dragStartX;
    if (Math.abs(delta) > 3) {
      this.moved = true;
    }

    this.dragOffset = Math.max(0, Math.min(this.travelPx, this.dragStartOffset + delta));
    this.applyDragTransform(this.dragOffset);
  }

  @HostListener('pointerup', ['$event'])
  @HostListener('pointercancel', ['$event'])
  onPointerUp(event: PointerEvent): void {
    if (!this.dragging || event.pointerId !== this.activePointerId) return;

    this.host.nativeElement.releasePointerCapture(event.pointerId);
    this.activePointerId = null;
    this.dragging = false;

    let selected: string;
    if (this.moved) {
      selected = this.dragOffset >= this.travelPx / 2 ? this.locales[1] : this.locales[0];
    } else {
      const rect = this.host.nativeElement.getBoundingClientRect();
      selected = event.clientX - rect.left >= rect.width / 2 ? this.locales[1] : this.locales[0];
    }

    this.suppressClick = true;
    this.selectLocale(selected);
    this.moved = false;
  }

  onBtnClick(locale: string, event: Event): void {
    event.stopPropagation();
    if (this.suppressClick) {
      this.suppressClick = false;
      return;
    }
    this.selectLocale(locale);
  }

  private selectLocale(locale: string): void {
    this.syncVisualState(locale);
    if (locale !== this.locale) {
      this.locale = locale;
      this.localeChange.emit(locale);
    }
  }

  private syncVisualState(locale: string = this.locale): void {
    this.updateTravel();
    const second = this.isSecond(locale);
    this.indicatorTransform = `translateX(${second ? this.travelPx : 0}px)`;
    this.firstActive = !second;
    this.secondActive = second;
  }

  private applyDragTransform(offset: number): void {
    this.indicatorTransform = `translateX(${offset}px)`;
    this.firstActive = offset < this.travelPx / 2;
    this.secondActive = offset >= this.travelPx / 2;
  }

  private isSecond(locale: string): boolean {
    return locale === this.locales[1];
  }

  private updateTravel(): void {
    const width = this.host.nativeElement.clientWidth;
    this.travelPx = Math.max(0, width / 2 - LangSegmentComponent.INSET_PX);
  }
}
